/**
* 管理端 MSW 桩 handlers（扫码登录/退出）。
 *
 * 扫码登录桩（对齐 admin-wechat-qr-login 后端契约）：
 * - POST /auth/wechat-login/qrcode 返回 mock:// 占位二维码（前端渲染员工点选列表）；
 * - GET  /auth/wechat-login/sessions/:token 恒返回 confirmed + 登录载荷；
 * - GET  /auth/wechat-login/mock-staffs 返回两名可选员工；
 * - POST /auth/wechat-login/mock-login 按staffId 回显姓名（未知 id 返回 400）。
 * - GET  /auth/permissions 返回登录后的权限快照，避免测试绕过 MSW 访问真实网络。
 * 退出桩：返回成功。
 * 异常场景（401/403/500/空态/加载失败/unregistered/expired）由测试用 server.use 临时覆盖（见 msw-scenarios.spec.ts）。
* 覆盖 admin-web-shell:SC-26 + identity-access:SC-65~SC-70。
 */
import { http, HttpResponse } from 'msw';

import { paymentEntryHandlers } from './payment-entries';

/**
 * 桩数据固定 UUID（合法格式，避免与真实后端 uuid 列类型冲突）。
 * mock 模式自洽使用，不代表真实库记录。
 */
const MOCK_ORG_ID = '00000000-0000-4000-8000-000000000001';
const MOCK_CAMPUS_IDS = [
  '00000000-0000-4000-8000-0000000000a1',
  '00000000-0000-4000-8000-0000000000a2',
  '00000000-0000-4000-8000-0000000000a3',
] as const;
/** 合法 UUID staffId（作 callerId，operation_logs.operator_id uuid 字段需合法格式）。 */
const STAFF_UUID = '00000000-0000-1000-8000-000000000001';

/** 桩员工（fake 模式扫码登录点选列表；2-2 起真实数据从后端 mock-staffs 获取）。 */
const mockLoginStaffs = [
  { id: STAFF_UUID, staffNo: 'staff-zhangsan', name: '张三' },
  { id: '00000000-0000-1000-8000-000000000002', staffNo: 'staff-lisi', name: '李四' },
];

/** 按员工 id 组装登录载荷桩（mock-login 与 sessions confirmed 共用）。 */
function mockLoginPayload(staffId: string) {
  const staff = mockLoginStaffs.find((s) => s.id === staffId) ?? mockLoginStaffs[0]!;
  return {
    token: `sess-${staff.id}`,
    staffId: staff.id,
    name: staff.name,
    organizationId: MOCK_ORG_ID,
    campusGrants: [
      { campusId: MOCK_CAMPUS_IDS[0], roleIds: ['00000000-0000-1000-8000-0000000000r1'] },
    ],
  };
}

export const adminHandlers = [
  http.post('/api/auth/wechat-login/qrcode', () =>
    HttpResponse.json({
      success: true,
      data: {
        sceneToken: 'mock-scene-token-1',
        qrcodeUrl: 'mock://wechat-login/mock-scene-token-1',
        expiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
      },
      timestamp: 't',
    }),
  ),
  http.get('/api/auth/wechat-login/sessions/:token', () =>
    HttpResponse.json({
      success: true,
      data: { status: 'confirmed', login: mockLoginPayload(STAFF_UUID) },
      timestamp: 't',
    }),
  ),
  http.get('/api/auth/wechat-login/mock-staffs', () =>
    HttpResponse.json({
      success: true,
      data: { items: mockLoginStaffs },
      timestamp: 't',
    }),
  ),
  http.post('/api/auth/wechat-login/mock-login', async ({ request }) => {
    const body = (await request.json().catch(() => ({}))) as { staffId?: string };
    if (!body.staffId || !mockLoginStaffs.some((s) => s.id === body.staffId)) {
      return HttpResponse.json(
        {
          success: false,
          error: { code: 'identity.staff_not_found', message: '员工不存在' },
          timestamp: 't',
        },
        { status: 400 },
      );
    }
    return HttpResponse.json({
      success: true,
      data: mockLoginPayload(body.staffId),
      timestamp: 't',
    });
  }),
  http.post('/api/auth/logout', () =>
    HttpResponse.json({ success: true, data: { success: true }, timestamp: 't' }),
  ),
  /**
   * 权限快照桩（对齐后端 GET /auth/permissions）。
   * staffId/campusScope 与 mock-login 返回一致；menuKeys 取真实系统管理员全菜单列表
   * （蓝本：后端 fake 登录后 GET /auth/permissions 实测响应），保证 MSW 态侧边栏菜单完整。
   * 既有缺陷修复：登录链路已 mock 唯独漏 permissions，致 MSW 态登录后 401 踢回登录页。
   *
   * ⚠️ 仅 UI 开发用（VITE_USE_MOCK=true）：本桩写死全菜单键（且含 ADM-00/03 等弃用键、
   * 无 menuPermissions 动作快照），不代表任何真实角色的权限——**权限相关验证必须
   * VITE_USE_MOCK=false 直连真实后端**，用本桩调试权限问题必然得到误导性结论。
   */
  http.get('/api/auth/permissions', () =>
    HttpResponse.json({
      success: true,
      data: {
        staffId: STAFF_UUID,
        positions: ['CAMPUS_ADMIN'],
        campusScope: MOCK_CAMPUS_IDS.join(','),
        menuKeys: [
          'ADM-00', 'ADM-01', 'ADM-02', 'ADM-03', 'ADM-04', 'ADM-05', 'ADM-06', 'ADM-07',
          'ADM-09', 'ADM-14', 'ADM-15', 'ADM-16', 'ADM-18', 'ADM-20', 'ADM-21',
          'ADM-22', 'ADM-23', 'ADM-24', 'ADM-25', 'ADM-26', 'ADM-29', 'ADM-31', 'ADM-32',
        ],
        hasAllOrgScope: true,
        grantedCampusIds: [MOCK_CAMPUS_IDS[0]],
      },
      timestamp: 't',
    }),
  ),

  /**
   * 校区列表桩（org-campus:SC-19/SC-20/SC-22 开发态 Mock）。
   * 返回符合后端 ApiResponse<Paginated<CampusItem>> 契约的分页数据。
   */
  http.get('/api/campuses', ({ request }) => {
    const url = new URL(request.url);
    const name = url.searchParams.get('name') ?? '';
    const code = url.searchParams.get('code') ?? '';
    const status = url.searchParams.get('status') ?? '';
    const page = Number(url.searchParams.get('page') ?? '1');
    const pageSize = Number(url.searchParams.get('pageSize') ?? '10');

    let items = mockCampuses.filter(
      (c) => (!name || c.name.includes(name)) && (!code || c.code.includes(code)),
    );
    if (status) {
      items = items.filter((c) => c.status === status);
    }
    const total = items.length;
    const start = (page - 1) * pageSize;
    const paged = items.slice(start, start + pageSize);

    return HttpResponse.json({
      success: true,
      data: { items: paged, total, page, pageSize },
      timestamp: 't',
    });
  }),

  /** 校区详情桩（org-campus:SC-23）。 */
  http.get('/api/campuses/:id', ({ params }) => {
    const campus = mockCampuses.find((c) => c.id === params.id);
    if (!campus) {
      return HttpResponse.json(
        {
          success: false,
          error: { code: 'org-campus.campus_not_found', message: '校区不存在' },
          timestamp: 't',
        },
        { status: 404 },
      );
    }
    return HttpResponse.json({ success: true, data: campus, timestamp: 't' });
  }),

  /** 新建校区桩（org-campus:SC-05）。 */
  http.post('/api/campuses', async ({ request }) => {
    const body = (await request.json().catch(() => ({}))) as {
      code?: string;
      name?: string;
      address?: string;
      timezone?: string;
    };
    if (body.code && mockCampuses.some((c) => c.code === body.code)) {
      return HttpResponse.json(
        {
          success: false,
          error: {
            code: 'org-campus.campus_code_duplicate',
            message: '校区编码已存在',
          },
          timestamp: 't',
        },
        { status: 409 },
      );
    }
    const campus = {
      id: crypto.randomUUID(),
      organizationId: MOCK_ORG_ID,
      code: body.code ?? '',
      name: body.name ?? '',
      address: body.address ?? null,
      timezone: body.timezone ?? 'Asia/Shanghai',
      status: 'ACTIVE' as const,
      version: 1,
      staffCount: 0,
      studentCount: 0,
      activeClassCount: 0,
    };
    mockCampuses.push(campus);
    return HttpResponse.json(
      { success: true, data: campus, timestamp: 't' },
      { status: 201 },
    );
  }),

  /** 编辑校区桩（org-campus:SC-11）。 */
  http.patch('/api/campuses/:id', async ({ params, request }) => {
    const campus = mockCampuses.find((c) => c.id === params.id);
    if (!campus) {
      return HttpResponse.json(
        {
          success: false,
          error: { code: 'org-campus.campus_not_found', message: '校区不存在' },
          timestamp: 't',
        },
        { status: 404 },
      );
    }
    const body = (await request.json().catch(() => ({}))) as {
      name?: string;
      address?: string;
      timezone?: string;
    };
    Object.assign(campus, body);
    campus.version += 1;
    return HttpResponse.json({ success: true, data: campus, timestamp: 't' });
  }),

  /** 停用校区桩（org-campus:SC-14）。 */
  http.post('/api/campuses/:id/suspend', ({ params }) => {
    const campus = mockCampuses.find((c) => c.id === params.id);
    if (!campus) {
      return HttpResponse.json(
        {
          success: false,
          error: { code: 'org-campus.campus_not_found', message: '校区不存在' },
          timestamp: 't',
        },
        { status: 404 },
      );
    }
    campus.status = 'SUSPENDED';
    return HttpResponse.json({ success: true, data: campus, timestamp: 't' });
  }),

  /** 启用校区桩（org-campus:SC-17）。 */
  http.post('/api/campuses/:id/activate', ({ params }) => {
    const campus = mockCampuses.find((c) => c.id === params.id);
    if (!campus) {
      return HttpResponse.json(
        {
          success: false,
          error: { code: 'org-campus.campus_not_found', message: '校区不存在' },
          timestamp: 't',
        },
        { status: 404 },
      );
    }
    campus.status = 'ACTIVE';
    return HttpResponse.json({ success: true, data: campus, timestamp: 't' });
  }),

  /** 当前机构信息桩（org-campus:SC-01）。 */
  http.get('/api/organizations/current', () =>
    HttpResponse.json({
      success: true,
      data: {
        id: MOCK_ORG_ID,
        name: '小象教育',
        status: 'ACTIVE',
        defaultTimezone: 'Asia/Shanghai',
        defaultProvince: '四川省',
        defaultCity: '成都市',
      },
      timestamp: 't',
    }),
  ),

  http.get('/api/revenue-statistics/sku-breakdown', () =>
    HttpResponse.json({
      success: true,
      data: { items: [] },
      timestamp: 't',
    }),
  ),
  http.get('/api/revenue-statistics', () =>
    HttpResponse.json({
      success: true,
      data: {
        campusId: null,
        dateFrom: null,
        dateTo: null,
        receivableAmountMinor: 0,
        paidAmountMinor: 0,
        revenueAmountMinor: 0,
        unpaidAmountMinor: 0,
        reversedAmountMinor: 0,
        discountAmountMinor: 0,
      },
      timestamp: 't',
    }),
  ),
  /**
   * 运营首页桩（operations-dashboard:ADM-01）。
   * 九类计数全零 + 空课次 + 待办四项 0；测试经 server.use 覆盖具体场景。
   */
  http.get('/api/operations/dashboard', () =>
    HttpResponse.json({
      success: true,
      data: {
        campusId: null,
        statDate: new Date().toISOString().slice(0, 10),
        enrollmentCount: 0,
        trialingCount: 0,
        paidOrderCount: 0,
        pendingPlacementCount: 0,
        inClassCount: 0,
        pendingDepositCount: 0,
        pendingFullCount: 0,
        pendingFinalCount: 0,
        overdueFinalCount: 0,
        undeterminedCount: 0,
        todaySessionCount: 0,
        todaySessions: [],
        pendingDeductionCount: 0,
        deductionExceptionCount: 0,
        fundExceptionCount: 0,
        todos: [
          { type: 'PENDING_DEDUCTION', count: 0 },
          { type: 'DEDUCTION_EXCEPTION', count: 0 },
          { type: 'FUND_EXCEPTION', count: 0 },
          { type: 'OVERDUE_FINAL', count: 0 },
        ],
      },
      timestamp: 't',
    }),
  ),
  http.get('/api/orders', () =>
    HttpResponse.json({
      success: true,
      data: { items: [], total: 0, page: 1, pageSize: 10 },
      timestamp: 't',
    }),
  ),

  ...paymentEntryHandlers,
];

/** 校区桩数据项类型（对齐后端 CampusItem 契约）。 */
interface MockCampus {
  id: string;
  organizationId: string;
  code: string;
  name: string;
  address: string | null;
  timezone: string;
  status: 'ACTIVE' | 'SUSPENDED';
  version: number;
  staffCount: number;
  studentCount: number;
  activeClassCount: number;
}

/** 校区桩数据（开发态可见，org-campus:SC-19 Mock）。 */
const mockCampuses: MockCampus[] = [
  {
    id: MOCK_CAMPUS_IDS[0],
    organizationId: MOCK_ORG_ID,
    code: 'GM',
    name: '高新校区',
    address: '成都市高新区天府大道',
    timezone: 'Asia/Shanghai',
    status: 'ACTIVE',
    version: 1,
    staffCount: 12,
    studentCount: 156,
    activeClassCount: 8,
  },
  {
    id: MOCK_CAMPUS_IDS[1],
    organizationId: MOCK_ORG_ID,
    code: 'WJ',
    name: '武侯校区',
    address: '成都市武侯区人民南路',
    timezone: 'Asia/Shanghai',
    status: 'ACTIVE',
    version: 1,
    staffCount: 8,
    studentCount: 98,
    activeClassCount: 5,
  },
  {
    id: MOCK_CAMPUS_IDS[2],
    organizationId: MOCK_ORG_ID,
    code: 'TF',
    name: '天府校区',
    address: '成都市天府新区科学城',
    timezone: 'Asia/Shanghai',
    status: 'ACTIVE',
    version: 2,
    staffCount: 0,
    studentCount: 0,
    activeClassCount: 0,
  },
];
